import React, { useState, useEffect } from 'react';
import db from '../database/db';
import './HistoryScreen.css';

function HistoryScreen({ userId }) {
  const [trainings, setTrainings] = useState([]);
  const [stats, setStats] = useState({
    totalTrainings: 0,
    totalDays: 0,
    averageWeight: 0,
    maxWeight: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const trainingsData = await db.getAllTrainings(userId);
      
      // Cargar ejercicios y series adicionales para cada entrenamiento
      const trainingsWithData = await Promise.all(
        trainingsData.map(async (training) => {
          const exercises = await db.getExercisesByTraining(training.id);
          const additionalSeries = await db.getSeriesByTraining(training.id);
          return { ...training, exercises, additionalSeries };
        })
      );

      setTrainings(trainingsWithData);

      // Calcular estadísticas
      if (trainingsData.length > 0) {
        // Obtener pesos de las series adicionales o usar un valor por defecto
        const weights = trainingsWithData.map(t => {
          if (t.additionalSeries && t.additionalSeries.length > 0) {
            return parseFloat(t.additionalSeries[t.additionalSeries.length - 1].weight);
          }
          return 0; // Si no hay series adicionales, no contamos el peso
        }).filter(w => w > 0);
        
        const uniqueDates = new Set(
          trainingsData.map(t => t.date.split('T')[0])
        );

        setStats({
          totalTrainings: trainingsData.length,
          totalDays: uniqueDates.size,
          averageWeight: weights.length > 0 
            ? Math.round((weights.reduce((a, b) => a + b, 0) / weights.length) * 2) / 2 
            : 0,
          maxWeight: weights.length > 0 ? Math.max(...weights) : 0
        });
      } else {
        setStats({
          totalTrainings: 0,
          totalDays: 0,
          averageWeight: 0,
          maxWeight: 0
        });
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="history-screen">
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="history-screen">
      <div className="history-container">
        <h1 className="history-title">Historial</h1>

        {stats.totalTrainings > 0 && (
          <div className="stats-section">
            <div className="stat-card">
              <div className="stat-value">{stats.totalTrainings}</div>
              <div className="stat-label">Entrenamientos</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalDays}</div>
              <div className="stat-label">Días</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.averageWeight} kg</div>
              <div className="stat-label">Peso Promedio</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.maxWeight} kg</div>
              <div className="stat-label">Peso Máximo</div>
            </div>
          </div>
        )}

        <div className="history-list">
          {trainings.length === 0 ? (
            <div className="no-history">
              <p>Aún no hay entrenamientos registrados.</p>
              <p>Comienza tu primer entrenamiento desde la pantalla de entrenamiento.</p>
            </div>
          ) : (
            trainings.map((training) => (
              <div key={training.id} className="history-item">
                <div className="history-header">
                  <div className="history-date">{formatDate(training.date)}</div>
                  {training.additionalSeries && training.additionalSeries.length > 0 && (
                    <div className="history-weight">
                      {training.additionalSeries[training.additionalSeries.length - 1].weight} kg
                    </div>
                  )}
                </div>
                
                <div className="history-series">
                  <div className="series-item">
                    <span className="series-label">Serie 1 (Bilbo):</span>
                    <span className="series-value">{training.series1_reps} reps</span>
                  </div>
                  {training.additionalSeries && training.additionalSeries.length > 0 && (
                    <>
                      {training.additionalSeries.map((series, index) => (
                        <div key={index} className="series-item">
                          <span className="series-label">Serie {series.series_number}:</span>
                          <span className="series-value">
                            {series.weight} kg × {series.reps} reps
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {training.exercises && training.exercises.length > 0 && (
                  <div className="history-exercises">
                    <strong>Ejercicios del entrenamiento:</strong>
                    <ul>
                      {training.exercises.map((exercise, index) => (
                        <li key={index}>
                          {exercise.exercise_name}: {exercise.weight} kg × {exercise.reps} reps
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {training.comments && (
                  <div className="history-comments">
                    <strong>Comentarios:</strong>
                    <p>{training.comments}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default HistoryScreen;
